import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  Camera, Heart, Loader2, Plus, Trophy, Upload, Trash2, ImageOff,
} from "lucide-react";

const PERIODS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all", label: "All Time" },
];
const SORTS = [
  { id: "top", label: "Top" },
  { id: "new", label: "New" },
];

function VoteButton({ showcase, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [voted, setVoted] = useState(showcase.voted_by_viewer);
  const [count, setCount] = useState(showcase.votes_count || 0);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to vote");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/showcases/${showcase.showcase_id}/vote`);
      setVoted(data.voted);
      setCount(data.votes_count);
      onChange?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={busy}
      data-testid={`showcase-vote-btn-${showcase.showcase_id}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        voted
          ? "bg-rose-500/15 text-rose-400 ring-1 ring-rose-500/30"
          : "bg-zinc-800/60 text-zinc-300 hover:bg-zinc-800 ring-1 ring-white/5"
      }`}
    >
      <Heart className={`w-4 h-4 ${voted ? "fill-current" : ""}`} />
      {count}
    </button>
  );
}

function ShowcaseCard({ showcase, currentUserId, onDeleted }) {
  const isOwner = currentUserId && showcase.user_id === currentUserId;
  const [count, setCount] = useState(showcase.votes_count || 0);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm("Delete this showcase? This cannot be undone.")) return;
    try {
      await api.delete(`/showcases/${showcase.showcase_id}`);
      toast.success("Showcase deleted");
      onDeleted?.(showcase.showcase_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      data-testid={`showcase-card-${showcase.showcase_id}`}
      className="group bg-zinc-900 ring-1 ring-white/5 rounded-2xl overflow-hidden hover:ring-rose-500/30 transition-all"
    >
      <Link to={`/showcases/${showcase.showcase_id}`} className="block relative aspect-square bg-black">
        <img
          src={resolveImageUrl(showcase.photo_url)}
          alt={showcase.title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white font-semibold text-sm leading-tight line-clamp-2">{showcase.title}</h3>
        </div>
      </Link>
      <div className="flex items-center justify-between p-3">
        <Link
          to={showcase.user_profile_slug ? `/collector/${showcase.user_profile_slug}` : "#"}
          className="flex items-center gap-2 min-w-0 hover:text-rose-400 transition-colors"
        >
          {showcase.user_profile_photo ? (
            <img src={resolveImageUrl(showcase.user_profile_photo)} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-zinc-800 grid place-items-center text-xs text-zinc-300">
              {(showcase.user_name || "?")[0]}
            </div>
          )}
          <span className="text-zinc-300 text-sm truncate">{showcase.user_name}</span>
        </Link>
        <div className="flex items-center gap-1.5">
          <VoteButton showcase={showcase} onChange={(d) => setCount(d.votes_count)} />
          {isOwner && (
            <button
              onClick={handleDelete}
              data-testid={`showcase-delete-btn-${showcase.showcase_id}`}
              className="p-1.5 rounded-full text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateShowcaseDialog({ open, onOpenChange, onCreated }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");

  const reset = () => {
    setFile(null); setPreview(null); setTitle(""); setCaption("");
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return toast.error("Pick a photo");
    if (!title.trim()) return toast.error("Add a title");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("caption", caption);
    try {
      const { data } = await api.post("/showcases", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Showcase posted");
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="showcase-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-zinc-100">Post a showcase photo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="showcase-create-file-input"
          />
          {!preview ? (
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full aspect-[4/3] bg-zinc-900 ring-1 ring-zinc-800 rounded-xl flex flex-col items-center justify-center text-zinc-400 hover:ring-rose-500/30 hover:text-zinc-200 transition-colors"
              data-testid="showcase-create-pick-photo"
            >
              <Camera className="w-10 h-10 mb-2" />
              <p className="text-sm">Pick a photo</p>
              <p className="text-xs text-zinc-500 mt-1">Up to 8 MB · jpg / png / webp</p>
            </button>
          ) : (
            <div className="relative">
              <img src={preview} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
              <button
                onClick={() => { setFile(null); setPreview(null); }}
                className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs bg-black/70 text-white"
              >
                Change
              </button>
            </div>
          )}
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 120))}
            placeholder="Give it a title (e.g. 'Hakosuka night drive')"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="showcase-create-title"
          />
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 600))}
            placeholder="Optional caption…"
            rows={3}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 resize-none"
            data-testid="showcase-create-caption"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy || !file || !title.trim()}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="showcase-create-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Post
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Leaderboard({ period }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/showcases/leaderboard?period=${period}&limit=10`).then((r) => setItems(r.data.leaderboard || []));
  }, [period]);

  if (!items.length) return null;
  return (
    <div className="bg-zinc-900 ring-1 ring-amber-500/10 rounded-2xl p-5" data-testid="showcase-leaderboard">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-400" />
        <h3 className="font-heading text-sm font-semibold text-zinc-100">Top photographers · {period}</h3>
      </div>
      <ol className="space-y-2">
        {items.map((row, idx) => (
          <li key={row.user_id} className="flex items-center gap-3" data-testid={`showcase-leaderboard-row-${idx}`}>
            <span className={`text-xs font-bold w-5 text-center ${idx < 3 ? "text-amber-400" : "text-zinc-500"}`}>{idx + 1}</span>
            {row.user_profile_photo ? (
              <img src={resolveImageUrl(row.user_profile_photo)} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-zinc-800 grid place-items-center text-xs text-zinc-300">
                {(row.user_name || "?")[0]}
              </div>
            )}
            <Link
              to={row.user_profile_slug ? `/collector/${row.user_profile_slug}` : "#"}
              className="text-zinc-200 text-sm hover:text-rose-400 truncate flex-1"
            >
              {row.user_name}
            </Link>
            <span className="text-zinc-400 text-xs">{row.total_votes} votes · {row.showcase_count} photos</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function ShowcasesPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("week");
  const [sort, setSort] = useState("top");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/showcases?period=${period}&sort=${sort}&limit=24`);
      setItems(data.results || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, sort]);

  return (
    <div data-testid="showcases-page" className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 animate-fadeIn">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-rose-400 text-xs uppercase tracking-[0.3em] font-semibold">Showcases</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100 mt-2">The community gallery</h1>
          <p className="text-zinc-400 mt-1">Drop your best diecast photos. Vote for the ones that make you stop scrolling.</p>
        </div>
        {user && (
          <Button
            onClick={() => setOpenCreate(true)}
            className="bg-rose-600 hover:bg-rose-500 text-white font-semibold"
            data-testid="showcase-open-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Post a photo
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-5">
        <div>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <Tabs value={period} onValueChange={setPeriod} data-testid="showcase-period-tabs">
              <TabsList className="bg-zinc-900 border border-zinc-800">
                {PERIODS.map((p) => (
                  <TabsTrigger key={p.id} value={p.id} className="text-xs" data-testid={`showcase-period-${p.id}`}>{p.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Tabs value={sort} onValueChange={setSort} data-testid="showcase-sort-tabs">
              <TabsList className="bg-zinc-900 border border-zinc-800">
                {SORTS.map((s) => (
                  <TabsTrigger key={s.id} value={s.id} className="text-xs" data-testid={`showcase-sort-${s.id}`}>{s.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="aspect-square rounded-2xl bg-zinc-900 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-12 text-center text-zinc-400" data-testid="showcase-empty">
              <ImageOff className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
              <p>No showcases yet for this period.</p>
              {user && (
                <Button onClick={() => setOpenCreate(true)} variant="link" className="text-rose-400 hover:text-rose-300 mt-2">
                  Be the first to post →
                </Button>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {items.map((s) => (
                <ShowcaseCard
                  key={s.showcase_id}
                  showcase={s}
                  currentUserId={user?.user_id}
                  onDeleted={(id) => setItems((prev) => prev.filter((it) => it.showcase_id !== id))}
                />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Leaderboard period={period} />
        </div>
      </div>

      <CreateShowcaseDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={(s) => setItems((prev) => [s, ...prev])}
      />
    </div>
  );
}
